import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SupportedChainId } from '@orionprotocol/sdk';
import { Icon, Select, SelectOption, useIsMobile } from '@orionprotocol/ui';
import {
  isChainInCurrentEnvironment,
  isSupportedChainId,
} from '@orionprotocol/utils';

import { NETWORK_CHANGED, useAnalytics } from '../../../shared/analytics';
import { useEnv } from '../../../shared/hooks';
import { appSlice } from '../../../shared/redux/slices';

import styles from './HeaderChangeNetwork.module.scss';
import { Network } from './types';

type Props = {
  networks: Network[];
  initialSelectedItem: Network;
  appChainId: string;
};

export function HeaderChangeNetworkSelect({
  networks,
  initialSelectedItem,
  appChainId,
}: Props): JSX.Element {
  const dispatch = useDispatch();
  const { sendEvent } = useAnalytics();
  const { envs, env } = useEnv();
  const isMobile = useIsMobile(480);
  const [selectedNetworkId, setSelectedNetworkId] = useState<SupportedChainId>(
    initialSelectedItem.id,
  );

  const handleNetworkSelect = (newNetworkId: string): void => {
    if (appChainId !== newNetworkId && isSupportedChainId(newNetworkId)) {
      if (
        env !== undefined &&
        isChainInCurrentEnvironment(envs, env, newNetworkId)
      ) {
        setSelectedNetworkId(newNetworkId);
        sendEvent(NETWORK_CHANGED, {
          newNetwork: newNetworkId,
        });
        dispatch(appSlice.actions.setAppNetwork(newNetworkId));
      }
    }
  };

  // Handle network update from other places other than the network selector
  // e.g. from Connect wallet popup
  useEffect(() => {
    const updatedNetwork = networks.find(
      (network) => network.id === appChainId,
    );

    if (updatedNetwork) {
      setSelectedNetworkId(updatedNetwork.id);
    }
  }, [networks, setSelectedNetworkId, appChainId, dispatch]);

  const options: SelectOption[] = Object.values(networks).map(
    ({ id, icon, code }) => ({
      value: id,
      label: <span className={styles.label}>{code}</span>,
      icon:
        icon !== undefined ? (
          <Icon icon={icon} className={styles.networkButtonIcon} />
        ) : undefined,
    }),
  );

  return (
    <Select
      value={selectedNetworkId}
      options={options}
      className={styles.select}
      buttonClassName={styles.button}
      dropdownClassName={styles.dropdown}
      labelClassName={styles.networkLabel}
      arrowClassName={styles.buttonArrow}
      checkmarkClassName={styles.selectedIcon}
      itemClassName={styles.networkItem}
      customArrow={null}
      showOnlyIcon={isMobile}
      onChange={handleNetworkSelect}
      placeholder="Select chain"
    />
  );
}
